
import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import Atlas from './pages/Atlas';
import Manifesto from './pages/Manifesto';
import Articles from './pages/Articles';
import ArticleDetail from './pages/ArticleDetail';
import Contacts from './pages/Contacts';
import Almanac from './pages/Almanac';
import Legal from './pages/Legal';
import Tools from './pages/Tools';
import NotFound from './pages/NotFound';
import Intro from './components/Intro';
import CookieBanner from './components/CookieBanner';

// Wrapper component to handle route transitions
const AnimatedRoutes: React.FC = () => {
    const location = useLocation();

    return (
        <div key={location.pathname} className="animate-page-enter">
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/atlante" element={<Atlas />} />
                <Route path="/strumenti" element={<Tools />} />
                <Route path="/almanacco" element={<Almanac />} />
                <Route path="/manifesto" element={<Manifesto />} />
                <Route path="/articoli" element={<Articles />} />
                <Route path="/articoli/:id" element={<ArticleDetail />} />
                <Route path="/contatti" element={<Contacts />} />
                <Route path="/legal" element={<Legal />} />
                {/* Catch-all Route for 404 */}
                <Route path="*" element={<NotFound />} />
            </Routes>
        </div>
    );
};

// Main Content Component (Inside Router context)
const AppContent: React.FC = () => {
  const navigate = useNavigate();
  // Intro State: Defaults to TRUE on every refresh/load
  const [showIntro, setShowIntro] = useState(true);
  const [introKey, setIntroKey] = useState(0);

  useEffect(() => {
    // Listen for Logo Click event from Navbar to replay Intro
    const handleReplayIntro = () => {
        setIntroKey(prev => prev + 1);
        setShowIntro(true);
        // Navigate to home immediately behind the curtain so it's ready when revealed
        navigate('/');
        window.scrollTo(0, 0);
    };

    window.addEventListener('play-intro', handleReplayIntro);
    return () => window.removeEventListener('play-intro', handleReplayIntro);
  }, [navigate]);

  const handleIntroComplete = () => {
      setShowIntro(false);
      // FORCE REDIRECT TO HOME AFTER INTRO FINISHES
      // This ensures if user refreshes on /articoli, they see Intro -> then land on Home
      navigate('/');
  };

  return (
    <div className="min-h-screen bg-paper text-black selection:bg-black selection:text-white relative">
        
        {/* GLOBAL INTRO: Overlays everything on Refresh or Logo Click */}
        {showIntro && (
            <Intro key={introKey} onComplete={handleIntroComplete} />
        )}

        <Navbar />
        <AnimatedRoutes />
        
        <CookieBanner />

        <footer className="border-t border-gray-100 py-12 mt-12 bg-white">
           <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
              
              <div className="mb-4 md:mb-0 text-left">
                <p className="font-serif font-bold text-lg">CYBERUMANITÀ</p>
                <div className="text-[10px] text-gray-500 mt-2 font-sans leading-relaxed uppercase tracking-wider">
                  Developed & Managed by<br/>
                  <a href="https://www.export24.eu" target="_blank" rel="noopener noreferrer" className="text-black font-bold hover:underline">EXPORT24</a>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-8 text-xs uppercase tracking-widest text-gray-500 font-bold">
                 <Link to="/almanacco" className="hover:text-black transition-colors">Almanacco</Link>
                 <Link to="/strumenti" className="hover:text-black transition-colors">Strumenti & Tools</Link>
                 <Link to="/legal" className="hover:text-black transition-colors">Legal & Privacy</Link>
              </div>
           </div>
        </footer>
    </div>
  );
};

// Root App Component provides the Router
const App: React.FC = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
